import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable,
} from "@tanstack/react-table";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import ChevronSvg from "../../assets/svgTsx/chevron";
import { Select } from "@headlessui/react";
import { PAGE_SIZES } from "../../constants/miscConstants";

interface StyledTableProps {
  data: any[];
  columns: any[];
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
  pagination: PaginationState;
  rowCount: number;
}

export const StyledTableBisManualPag: FC<StyledTableProps> = ({
  data,
  columns,
  setPagination,
  pagination,
  rowCount,
}) => {
  const { t } = useTranslation(["shared", "common"]);
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    rowCount: rowCount,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
  });

  return (
    <div className="flex flex-1 overflow-auto">
      <div className="h-fit min-w-full overflow-auto rounded-lg border">
        <table
          className="layout-fixed min-w-full border-collapse divide-y divide-gray-200 overflow-hidden rounded-xl border whitespace-nowrap"
          cellSpacing="0"
          cellPadding="0"
        >
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className={`px-3 py-2 text-sm font-medium text-gray-600 sm:px-3 sm:py-3`}
                  >
                    <div
                      className={`${header.column.columnDef.meta || ""} flex flex-row items-center gap-2`}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <div className="flex flex-col items-center justify-center gap-0">
                          <ChevronSvg
                            className={`-rotate-90`}
                            color={header.column.getIsSorted() === "asc" ? "#000" : "#9ca3af"}
                            width={10}
                            height={10}
                          />
                          <ChevronSvg
                            className="text-gr rotate-90"
                            color={header.column.getIsSorted() === "desc" ? "#000" : "#9ca3af"}
                            width={10}
                            height={10}
                          />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="divide-y-2 divide-gray-300 md:divide-y md:divide-gray-200">
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className={`border border-solid border-gray-200 bg-white`}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`"whitespace-nowrap px-3 py-2 last:w-[0.1%] sm:px-3 sm:py-3`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-white px-4 py-2 sm:px-6 sm:py-4">
              <td
                key={1}
                className="px-4 py-2 text-sm font-medium text-gray-700 sm:px-6 sm:py-4"
                colSpan={columns.length}
              >
                <div className="flex flex-row gap-10">
                  <div className="flex gap-1 sm:gap-3">
                    <button
                      className={`rounded-lg border border-solid border-gray-200 text-xs sm:text-sm ${!table.getCanPreviousPage() ? "bg-gray-200" : "bg-white"} px-3 py-2 text-gray-700`}
                      onClick={() => table.previousPage()}
                      disabled={!table.getCanPreviousPage()}
                    >
                      {t("common:actions.previous")}
                    </button>
                    <button
                      className={`rounded-lg border border-solid border-gray-200 text-xs sm:text-sm ${!table.getCanNextPage() ? "bg-gray-200" : "bg-white"} px-3 py-2 text-gray-700`}
                      onClick={() => table.nextPage()}
                      disabled={!table.getCanNextPage()}
                    >
                      {t("common:actions.next")}
                    </button>
                  </div>
                  <div className="flex w-fit flex-row items-center gap-4">
                    <div className="flex w-full items-center">
                      <span className="hidden align-middle sm:block">
                        {t("table.pageOf", {
                          page: table.getState().pagination.pageIndex + 1,
                          pageCount: table.getPageCount().toLocaleString(),
                        })}
                      </span>
                      <span className="block align-middle sm:hidden">
                        {table.getState().pagination.pageIndex + 1}/
                        {table.getPageCount().toLocaleString()}
                      </span>
                    </div>
                    <Select
                      value={table.getState().pagination.pageSize}
                      onChange={(e) => {
                        table.setPageSize(Number(e.target.value));
                      }}
                      className="w-fit rounded-lg border border-gray-200 bg-white p-1"
                    >
                      {PAGE_SIZES.map((pageSize) => (
                        <option key={pageSize} value={pageSize}>
                          {pageSize}
                        </option>
                      ))}
                    </Select>
                    <span className="hidden md:block">{t("table.perPage")}</span>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
